import { FC } from "react";
import { Box, Divider, Paper, Typography } from "@mui/material";
import { MergerChip } from "../../../components/mergerChip";
import { EntityKind } from "../../../utils/entityPaths";
import {
  MERGER_ANSWER_FIELDS,
  MergerAnswers,
  NEEDED_LABELS
} from "../marketAccessFields";
import { MergerAttachments } from "../mergerAttachments";

type Props = {
  kind: EntityKind;
  entityId: string;
  mergerDescription?: string | null;
  answers?: MergerAnswers | null;
};

/**
 * Read-only view of what the merger was created with: description, Market Access answers and
 * documents. Shown on the info card and at the top of the Compliance dialog, so whoever works
 * the merger sees the decisions without opening an editor. Editing happens in the info card's
 * Edit merger dialog, never here: the Compliance dialog writes its own slice, and these answers
 * live on marketAccessLifecycle.
 */
export const MergerSetup: FC<Props> = ({
  kind,
  entityId,
  mergerDescription,
  answers
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    {/* No label: the panel heading already says this is the merger, and the description leads
        it, one step larger than the answers below. */}
    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
      {mergerDescription || "No merger description."}
    </Typography>
    <Divider />

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        columnGap: 3,
        rowGap: 1
      }}
    >
      {MERGER_ANSWER_FIELDS[kind].map((field) => {
        const value = answers?.[field.key];
        return (
          <Box key={field.key} sx={{ display: "flex", gap: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              {field.label}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {(value && (NEEDED_LABELS[value] ?? value)) || "N/A"}
            </Typography>
          </Box>
        );
      })}
    </Box>

    <Box>
      <Typography variant="caption" color="text.secondary">
        Merger documents
      </Typography>
      <MergerAttachments kind={kind} entityId={entityId} />
    </Box>
  </Box>
);

/**
 * MergerSetup framed for the top of a lifecycle dialog (the Compliance one, per the 2026-10-06
 * decision that the Market Access fields are shown in Compliance afterwards).
 */
export const MergerSetupPanel: FC<Props> = (props) => (
  <Paper variant="outlined" sx={{ padding: 2 }}>
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1.5, marginBottom: 2 }}
    >
      <Typography variant="subtitle1">Merger setup</Typography>
      <MergerChip />
    </Box>
    <MergerSetup {...props} />
  </Paper>
);
